/* eslint-disable max-lines */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useGetState, useMount, useUnmount, useUpdateEffect } from 'ahooks';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import { Icon } from '@fuxi/eevee-icon';
import { FrameAnimation } from 'common-package';
import { Button, Dialog } from 'antd-mobile';
import { Header, ServiceProcessCrumb, Toast } from '@/components';
import { useWebRtc } from '@/hooks/useWebRtc';
import dashedCircleImg from '@/assets/image/dashed-circle.png';
import dashedLineImg from '@/assets/image/dashed-line.png';
import { IssueDetectStep } from '@/components/service-process-crumb';
import service from '@/service';
import lineArrowImg from '@/assets/image/line-arrow.png';
import { useNav, useSocketIO } from '@/hooks';
import { ALL_PAGES, storagePrefixV2 } from '@/constant';

import cx from './index.module.scss';
import MarkerContent from './marker-content';

interface Props {}

type StepState = IssueDetectStep | 'Finished';

type PlaceState = 'Pending' | 'Done' | 'Running';

const STATE_TO_STEP: Record<GetTroubleShootStatesResponse['state'], StepState> = {
  Idle: 'DetectZeroDrift',
  CheckingDrift: 'DetectZeroDrift',
  Preparing: 'InstallDetectTools',
  WaitingInstallation: 'InstallDetectTools',
  WaitingPlaced: 'PlacePadQRCode',
  Running: 'PlacePadQRCode',
  Finished: 'Finished',
};

const IssueDetectTool: React.FC<Props> = () => {
  const nav = useNav();
  const currentDeviceId = useRef(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [currentStep, setCurrentStep] = useState<StepState>('DetectZeroDrift');
  const [isShowMarker, setIsShowMarker] = useState(false);
  const [isRTCConnected, setIsRTCConnected] = useState(false);
  const [isPlayVideo, setIsPlayVideo] = useState(false);
  const [isEnteringTroubleShoot, setIsEnteringTroubleShoot] = useState(false);
  const [isCompletingInstallation, setIsCompletingInstallation] = useState(false);
  const [isCompleteTroubleShooting, setIsCompleteTroubleShooting] = useState(false);
  const [currentCheckState, setCurrentCheckState, getCurrentCheckState] = useGetState<GetTroubleShootStatesResponse>(
    {} as any
  );
  const [markerCountdownShowedInfo, setMarkerCountdownShowedInfo] = useState<number[]>([]);
  const [currentTroubleShootState, setCurrentTroubleShootState] =
    useState<GetTroubleShootStatesResponse['state']>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const location = useLocation();

  const { socketInstance, initSocket, destroySocket } = useSocketIO({ autoCloseOnUnmount: true });

  const onRobotEvent: onRoboticEventListener = useCallback(event => {
    console.log('onRobotEvent', event);
    switch (event.name) {
      case 'trouble_shoot_state_change':
        const troubleShootState = event.details?.state;
        setCurrentTroubleShootState(troubleShootState);

        setCurrentStep(STATE_TO_STEP[troubleShootState]);
        setIsShowMarker(troubleShootState === 'Running');
        break;
      case 'trouble_shoot_error':
        !!event.details?.error_message && Toast.warn(event.details.error_message, { duration: 5000 });
        setMarkerCountdownShowedInfo(prev => prev?.filter(item => item !== getCurrentCheckState()?.checkNo));
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStateByNo = useCallback(
    (index: 0 | 1 | 2 | 3 | 4): PlaceState => {
      if (currentCheckState?.checkNo === null) {
        return 'Done';
      } else if (currentCheckState?.checkNo === index) {
        return currentCheckState?.state === 'Finished' ? 'Done' : 'Running';
      } else {
        return currentCheckState?.checkNo > index ? 'Done' : 'Pending';
      }
    },
    [currentCheckState]
  );

  const getCheckState = useCallback(async () => {
    const data = await socketInstance.current?.getTroubleShootStates();

    if (data.state === 'Idle') {
      socketInstance.current?.enterTroubleShoot().catch(() => {
        nav(ALL_PAGES.DEVICE_MANAGEMENT);
      });
    }

    setCurrentCheckState(data);
  }, [nav, socketInstance]);

  const init = useCallback(async () => {
    const { deviceId } = location.state || {};
    currentDeviceId.current = deviceId;
    if (!deviceId) {
      nav(ALL_PAGES.DEVICE_MANAGEMENT);
      return;
    }
    const { data: connectInfo } = await service.device.getConnectionInfo(deviceId);
    initSocket(connectInfo);

    socketInstance.current.onConnect(() => {
      console.log('onconnect');
      setIsSocketConnected(true);
      getCheckState();
    });

    socketInstance.current.onDisconnect(() => {
      setIsSocketConnected(false);
    });

    socketInstance.current.onRobotEvent(onRobotEvent);
  }, [getCheckState, initSocket, location.state, nav, onRobotEvent, socketInstance]);

  const handleQuit = useCallback(() => {
    Dialog.clear();
    Dialog.confirm({
      title: '确认退出误差排查？',
      content: '退出后将直接关闭误差排查程序',
      confirmText: '确认',
      onConfirm: async () => {
        nav(ALL_PAGES.DEVICE_MANAGEMENT);
      },
    });
  }, [nav]);

  const { start: startRTC, stop: stopRTC } = useWebRtc({
    socketInstance,
    videoRef,
    rtcAPIType: 'order-service',
    deviceId: location?.state?.deviceId,
    onVideoPlay: () => {
      setIsPlayVideo(true);
    },
    onRTCConnectionStateChange(state) {
      if (state === 'connected') {
        setIsRTCConnected(true);
      }
    },
  });

  // 监听重新返回页面事件
  // useUpdateEffect(() => {
  //   if (documentVisibility === 'visible') {
  //     socketInstance.current?.socket?.connect();
  //   }
  //   if (documentVisibility === 'hidden') {
  //     socketInstance.current?.socket?.close();
  //   }
  // }, [documentVisibility]);

  useMount(init);

  useUnmount(() => {
    stopRTC();
    currentDeviceId.current && service.lanInterface.exitTroubleShoot(currentDeviceId.current);
    destroySocket?.();
  });

  useUpdateEffect(() => {
    // 从marker跳回主界面时获取最新状态
    !isShowMarker && getCheckState();
  }, [isShowMarker]);

  useEffect(() => {
    if (currentStep === 'PlacePadQRCode' && !isShowMarker) {
      startRTC();
    } else {
      stopRTC();
      setIsPlayVideo(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, isShowMarker]);

  return (
    <div className={cx('IssueDetectTool')}>
      <Header
        backConfig={{
          text: '设备管理',
          action: handleQuit,
        }}
        title="视觉排查"
      />
      {isSocketConnected ? (
        <>
          <ServiceProcessCrumb
            isIssueDetectSteps={true}
            currentPageStep={currentStep === 'Finished' ? 'PlacePadQRCode' : currentStep}
          />
          <div className={cx('detect-container')}>
            {currentStep === 'DetectZeroDrift' ? (
              <div className={cx('DetectZeroDrift')}>
                <div className={cx('DetectZeroDrift-left')}></div>
                <div className={cx('DetectZeroDrift-right')}>
                  <div className={cx('tips-box')}>
                    <Icon name="warning" className={cx('tips-icon')} />
                    <span>请位于机械臂内侧，拍摄各轴标定点，保证“ </span>
                    <img src={lineArrowImg} className={cx('arrow-img')} />
                    <span>”箭头贴纸清晰可见</span>
                  </div>
                  <div className={cx('tips-img')}>
                    <div className={cx('img-item')} />
                    <div className={cx('img-item')} />
                    <div className={cx('img-item')} />
                  </div>
                </div>
              </div>
            ) : currentStep === 'InstallDetectTools' ? (
              <div className={cx('InstallDetectTools')}>
                <div className={cx('massage-head-type')} />
                <div className={cx('InstallDetectTools-left')} />
                <div className={cx('InstallDetectTools-right')}>
                  <div className={cx('tips-box')}>
                    <div className={cx('tips-text')}>
                      <div className={cx('tips-text-title')}>
                        <Icon className={cx('icon')} name="warning-filled" />
                        <div>安装理疗末端注意事项</div>
                      </div>
                      <div className={cx('tips-text-content')}>
                        <div className={cx('tips-text-box')}>
                          <span>1.</span>
                          <span>
                            当红点对齐<span className={cx('highlight')}>红色“锁定图标”</span>
                            ，并听到“咔哒”一声后确认安装到位
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={cx('tips-img')}>
                      <FrameAnimation
                        className={cx('head-install-img')}
                        url={storagePrefixV2 + '/animation/head-lock-animation-min.png'}
                        aspectRatio={1.677}
                        count={173}
                        duration={4}
                        width={200}
                      />
                    </div>
                  </div>
                  <div className={cx('dash-indicator')}>
                    <img src={dashedCircleImg} alt="" className={cx('dashed-circle')} />
                    <img src={dashedLineImg} alt="" className={cx('dashed-line')} />
                  </div>
                </div>
              </div>
            ) : (
              <div className={cx('PlacePadQRCode')}>
                <div className={cx('PlacePadQRCode-left')}>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    className={cx('player-content', { show: isRTCConnected && isPlayVideo })}
                  />

                  <div className={cx('guide-box')}>
                    <div className={cx('box-item')}>
                      <div className={cx('place-item', 'place-item-1', getStateByNo(0))}>
                        <div className={cx('mirror-bg')} />
                        <div className={cx('place-item-content')}>
                          {getStateByNo(0) === 'Done' ? (
                            <Icon name="success" />
                          ) : (
                            <span className={cx('index-num')}>1</span>
                          )}
                        </div>
                      </div>
                      <div className={cx('place-item', 'place-item-2', getStateByNo(1))}>
                        <div className={cx('mirror-bg')} />
                        <div className={cx('place-item-content')}>
                          {getStateByNo(1) === 'Done' ? (
                            <Icon name="success" />
                          ) : (
                            <span className={cx('index-num')}>2</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={cx('box-item')}>
                      <div className={cx('place-item', 'place-item-5', getStateByNo(4))}>
                        <div className={cx('mirror-bg')} />
                        <div className={cx('place-item-content')}>
                          {getStateByNo(4) === 'Done' ? (
                            <Icon name="success" />
                          ) : (
                            <span className={cx('index-num')}>5</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={cx('box-item')}>
                      <div className={cx('place-item', 'place-item-3', getStateByNo(2))}>
                        <div className={cx('mirror-bg')} />
                        <div className={cx('place-item-content')}>
                          {getStateByNo(2) === 'Done' ? (
                            <Icon name="success" />
                          ) : (
                            <span className={cx('index-num')}>3</span>
                          )}
                        </div>
                      </div>
                      <div className={cx('place-item', 'place-item-4', getStateByNo(3))}>
                        <div className={cx('mirror-bg')} />
                        <div className={cx('place-item-content')}>
                          {getStateByNo(3) === 'Done' ? (
                            <Icon name="success" />
                          ) : (
                            <span className={cx('index-num')}>4</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={cx('PlacePadQRCode-right')}>
                  {currentStep === 'Finished' ? (
                    <div className={cx('finished-wrapper')}>
                      <img src={storagePrefixV2 + '/issue-detect/issue-detect-ok.png'} alt="" />
                      <div>排查已完成，请联系售后工程师</div>
                    </div>
                  ) : (
                    <>
                      <div className={cx('header')}>
                        <div className={cx('main-tip')}>放置平板识别码</div>
                        <div className={cx('sub-tip')}>
                          请在下图提示，将平板屏幕朝上，竖直放置到右侧识别框中，然后点击「开始排查」按钮。
                        </div>
                      </div>
                      <div className={cx('content')}>
                        <video
                          muted
                          loop
                          autoPlay
                          poster={storagePrefixV2 + '/issue-detect/issue-detect-ani-poster.png'}
                          src={storagePrefixV2 + '/issue-detect/issue-detect-ani.mp4'}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            <div className={cx('btns')}>
              {currentStep === 'DetectZeroDrift' ? (
                <>
                  <Button className={cx('btn-item', 'cancel')} style={{ width: '132px' }} onClick={handleQuit}>
                    取消
                  </Button>
                  <Button
                    className={cx('btn-item', 'confirm')}
                    color="primary"
                    loading={isEnteringTroubleShoot}
                    style={{ width: '148px' }}
                    onClick={() => {
                      Dialog.clear();
                      Dialog.confirm({
                        title: '确认已与售后进行沟通？',
                        content: (
                          <div style={{ paddingBottom: '12px' }}>
                            零飘检查结果将影响机器问题排查，请确保
                            <br />
                            您已与售后人员进行了充分沟通
                          </div>
                        ),
                        confirmText: '确认',
                        onConfirm: async () => {
                          try {
                            setIsEnteringTroubleShoot(true);
                            await socketInstance.current?.completeCheckingDrift();
                          } finally {
                            setIsEnteringTroubleShoot(false);
                          }
                        },
                      });
                    }}
                  >
                    下一步
                  </Button>
                </>
              ) : currentStep === 'InstallDetectTools' ? (
                <>
                  <Button className={cx('btn-item', 'cancel')} style={{ width: '128px' }} onClick={handleQuit}>
                    取消
                  </Button>
                  <Button
                    className={cx('btn-item', 'confirm')}
                    color="primary"
                    disabled={currentTroubleShootState !== 'WaitingInstallation'}
                    loading={isCompletingInstallation}
                    style={{ width: '214px' }}
                    onClick={async () => {
                      try {
                        setIsCompletingInstallation(true);
                        await socketInstance.current?.completeTroubleShootInstallation();
                      } finally {
                        setIsCompletingInstallation(false);
                      }
                    }}
                  >
                    确认安装排查末端
                  </Button>
                </>
              ) : currentStep === 'PlacePadQRCode' ? (
                <>
                  <Button className={cx('btn-item', 'cancel')} style={{ width: '128px' }} onClick={handleQuit}>
                    取消
                  </Button>
                  <Button
                    className={cx('btn-item', 'confirm')}
                    color="primary"
                    loading={isCompleteTroubleShooting}
                    style={{ width: '214px' }}
                    onClick={async () => {
                      try {
                        setIsCompleteTroubleShooting(true);
                        await socketInstance.current?.completeTroubleShootPlacement();
                        setIsShowMarker(true);
                      } finally {
                        setIsCompleteTroubleShooting(false);
                      }
                    }}
                  >
                    开始排查
                  </Button>
                </>
              ) : currentStep === 'Finished' ? (
                <>
                  <Button className={cx('btn-item', 'cancel')} style={{ width: '128px' }} onClick={handleQuit}>
                    取消
                  </Button>
                  <Button
                    className={cx('btn-item', 'confirm')}
                    color="primary"
                    style={{ width: '214px' }}
                    onClick={() => nav(ALL_PAGES.DEVICE_MANAGEMENT)}
                  >
                    完成排查
                  </Button>
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
          {isShowMarker && (
            <MarkerContent
              socketInstance={socketInstance}
              directShowMarker={markerCountdownShowedInfo.includes(currentCheckState?.checkNo)}
              onShowMarker={() => setMarkerCountdownShowedInfo(prev => [...(prev || []), currentCheckState?.checkNo])}
            />
          )}
        </>
      ) : (
        <div className={cx('loading-wrapper')}>
          <Loading3QuartersOutlined spin className={cx('icon')} />
        </div>
      )}
    </div>
  );
};

export default IssueDetectTool;
